import {
  Button,
  Col,
  Row,
  Table,
  TableProps,
  Grid,
  Popconfirm,
  Alert,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import NarinBreadcrumb from "components/Desktop/Breadcrumb";
import NarinLayout from "components/NarinLayout";
import NInputNumber from "components/Desktop/NInputNumber";
import { IMAGE_NOT_FOUND } from "constants/common";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDebounce } from "utils/debounce";
import {
  useGetCart,
  useUpdateItemInCart,
  useDeleteItemInCart,
  useUpdateRedemptionItemInCart,
  useBulkDeleteItemsInCart,
} from "services/cart";
import useCartStore from "stores/cart";
import useUserStore from "stores/user";
import { ICartItem } from "types/cart";
import { RedemptionItem } from "types/redemption";
import MobileCartItem from "components/Mobile/MobileCartItem";
import ProductImage from "components/ProductImage";
// import {
//   validateCartRedemptionsAtCheckout,
//   hasRedemptionItems,
// } from "utils/redemptionValidation";

interface DataType {
  id: number;
  key: number;
  productName: string;
  price: string;
  qty: number;
  imageUrl: string;
  productId: number;
  productCode?: string;
  isRedemptionItem?: boolean;
  redemptionItem?: RedemptionItem;
}

const CartPage = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const [dataTable, setDataTable] = useState<DataType[]>([]);
  const [selectItems, setSelectItems] = useState<DataType[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isValidatingCart, setIsValidatingCart] = useState(false);
  const { user } = useUserStore();
  const { cartItemList } = useGetCart(user);
  const { updateItemInCartAsync } = useUpdateItemInCart();
  const { updateRedemptionItemInCartAsync } = useUpdateRedemptionItemInCart();
  const { deleteItemInCartAsync } = useDeleteItemInCart();
  const { bulkDeleteItemsInCartAsync } = useBulkDeleteItemsInCart();
  const { setProductItemsSelected } = useCartStore();
  const navigate = useNavigate();

  const location = useLocation();

  // Parse query parameters for default checked items
  const getProductIdsFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const products = searchParams.get("products");
    return products ? products.split(",").map((id) => parseInt(id, 10)) : [];
  }, [location.search]);

  useEffect(() => {
    if (cartItemList) {
      const formatData = cartItemList.map((cartItem: ICartItem) => {
        // Handle redemption items
        if (cartItem.redemptionItemId && cartItem.redemptionItem) {
          return {
            id: cartItem.id,
            key: cartItem.id,
            imageUrl: cartItem.redemptionItem.images?.[0] || "",
            productName: cartItem.redemptionItem.name,
            price: cartItem.redemptionItem.cashCost?.toString() || "0",
            qty: cartItem.quantity,
            productId: 0, // No product ID for redemption items
            isRedemptionItem: true,
            redemptionItem: cartItem.redemptionItem,
          };
        }

        // Handle regular products
        return {
          id: cartItem.id,
          key: cartItem.id,
          imageUrl: cartItem.productPrice?.product.images[0] || "",
          productName: cartItem.productPrice?.product.name || "",
          price: cartItem.productPrice?.price || "0",
          qty: cartItem.quantity,
          productId: cartItem.productPrice?.product.id || 0,
          productCode: cartItem.productPrice?.product.productCode || "",
          isRedemptionItem: false,
        };
      });
      setDataTable(formatData);

      // Check if we have product IDs in the query string
      const productIds = getProductIdsFromQuery();
      if (productIds.length > 0) {
        // Select items based on query parameters
        const itemsToSelect = formatData.filter((item) =>
          productIds.includes(item.productId)
        );
        if (itemsToSelect.length > 0) {
          setSelectItems(itemsToSelect);
        }
      }
    }
  }, [cartItemList, getProductIdsFromQuery]);

  // Debounced function to update cart item in the API
  const debouncedUpdateCart = useDebounce(
    async (cartItemId: number, quantity: number) => {
      try {
        await updateItemInCartAsync({
          cartItemId,
          quantity,
        });
      } catch (error) {
        console.error("Failed to update cart item:", error);
      }
    },
    500, // 500ms debounce time
    [updateItemInCartAsync]
  );

  // Debounced function to update redemption item in the API
  const debouncedUpdateRedemptionCart = useDebounce(
    async (cartItemId: number, quantity: number) => {
      try {
        await updateRedemptionItemInCartAsync({
          cartItemId,
          quantity,
        });
      } catch (error) {
        console.error("Failed to update redemption item:", error);
      }
    },
    500, // 500ms debounce time
    [updateRedemptionItemInCartAsync]
  );

  const handleOnChangeInput = useCallback(
    (qty: number, itemId: number) => {
      // Find the item by ID instead of using array index
      const itemIndex = dataTable.findIndex((item) => item.id === itemId);
      if (itemIndex === -1) {
        console.error(`Item with ID ${itemId} not found in dataTable`);
        return;
      }

      const updatedItem = { ...dataTable[itemIndex], qty };

      // Update UI immediately
      setDataTable((prev: DataType[]) => {
        const newData = [...prev];
        const targetIndex = newData.findIndex((item) => item.id === itemId);
        if (targetIndex !== -1) {
          newData[targetIndex] = updatedItem;
        }
        return newData;
      });

      // Update selected items if necessary
      setSelectItems((prev) => {
        if (prev.some((item) => item.id === updatedItem.id)) {
          return prev.map((item) =>
            item.id === updatedItem.id ? updatedItem : item
          );
        }
        return prev;
      });

      // Debounce the API call based on item type
      if (updatedItem.isRedemptionItem) {
        // For redemption items, use the cart item ID
        debouncedUpdateRedemptionCart(updatedItem.id, qty);
      } else {
        // For regular products, use the cart item ID (not product ID)
        debouncedUpdateCart(updatedItem.id, qty);
      }
    },
    [dataTable, debouncedUpdateRedemptionCart, debouncedUpdateCart]
  );

  const handleDeleteItem = useCallback(
    async (itemId: number) => {
      try {
        await deleteItemInCartAsync(itemId);
        // Remove the item from selectItems if it was selected
        setSelectItems((prev) => prev.filter((item) => item.id !== itemId));
      } catch (error) {
        console.error("Failed to delete cart item:", error);
      }
    },
    [deleteItemInCartAsync]
  );

  const handleBulkDelete = async () => {
    if (selectItems.length === 0) return;

    try {
      const itemIds = selectItems.map((item) => item.id);
      await bulkDeleteItemsInCartAsync(itemIds);
      // Clear selected items after successful deletion
      setSelectItems([]);
    } catch (error) {
      console.error("Failed to bulk delete cart items:", error);
    }
  };

  // Unified selection handlers for cross-section selection
  const handleProductSectionSelect = useCallback((selectedRows: DataType[]) => {
    // Keep existing redemption selections and update product selections
    setSelectItems((prev) => [
      ...prev.filter((item) => item.isRedemptionItem),
      ...selectedRows,
    ]);
  }, []);

  const handleRedemptionSectionSelect = useCallback(
    (selectedRows: DataType[]) => {
      // Keep existing product selections and update redemption selections
      setSelectItems((prev) => [
        ...prev.filter((item) => !item.isRedemptionItem),
        ...selectedRows,
      ]);
    },
    []
  );

  const handleUnifiedSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectItems([...dataTable]);
      } else {
        setSelectItems([]);
      }
    },
    [dataTable]
  );

  // Validate cart redemptions before checkout
  // const validateCartForCheckout = useCallback(async () => {
  //   if (!cartItemList || cartItemList.length === 0) {
  //     setValidationErrors([]);
  //     return true;
  //   }

  //   // Check if cart has redemption items
  //   if (!hasRedemptionItems(cartItemList)) {
  //     setValidationErrors([]);
  //     return true;
  //   }

  //   setIsValidatingCart(true);
  //   setValidationErrors([]);

  //   try {
  //     const validationResult = await validateCartRedemptionsAtCheckout(
  //       cartItemList
  //     );

  //     if (!validationResult.isValid) {
  //       setValidationErrors(validationResult.errors);
  //       return false;
  //     }

  //     setValidationErrors([]);
  //     return true;
  //   } catch (error) {
  //     console.error("Cart validation failed:", error);
  //     setValidationErrors(["Failed to validate cart. Please try again."]);
  //     return false;
  //   } finally {
  //     setIsValidatingCart(false);
  //   }
  // }, [cartItemList]);

  const handleSingleItemSelect = useCallback(
    (item: DataType, checked: boolean) => {
      setSelectItems((prev) =>
        checked
          ? [...prev, item]
          : prev.filter((selected) => selected.id !== item.id)
      );
    },
    []
  );

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "",
      dataIndex: "imageUrl",
      key: "imageUrl",
      width: "12%",
      render: (text: string, record: DataType) => {
        return (
          <ProductImage
            imageUrl={text}
            productCode={record.productCode}
            width={90}
            height={90}
            alt={text}
            className="object-contain"
            fallback={IMAGE_NOT_FOUND}
          />
        );
      },
    },
    {
      title: "สินค้า",
      dataIndex: "productName",
      key: "productName",
      width: "38%",
      render: (text: string) => <div>{text}</div>,
    },
    {
      title: "ราคาต่อหน่วย",
      dataIndex: "price",
      key: "price",
      width: "15%",
      render: (text: number, record: DataType) => {
        if (record.isRedemptionItem) {
          return (
            <div>
              <div className="text-primary-500">
                {record.redemptionItem?.pointsCost} คะแนน
              </div>
              {record.redemptionItem?.type === "HYBRID" && (
                <div className="text-sm">+ {text.toLocaleString()} บาท</div>
              )}
            </div>
          );
        }
        return <div>{text.toLocaleString()} บาท</div>;
      },
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      key: "qty",
      width: "15%",
      render: (qty: number, record: DataType) => {
        return (
          <NInputNumber
            value={qty}
            onChange={(e) => handleOnChangeInput(e, record.id)}
            disabled={false} // Enable quantity change for all items including redemption items
          />
        );
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "sum",
      key: "sum",
      width: "15%",
      render: (_: string, record: DataType) => {
        if (record.isRedemptionItem) {
          return (
            <div>
              <div className="text-primary-500">
                {record.qty * (record.redemptionItem?.pointsCost || 0)} คะแนน
              </div>
              {record.redemptionItem?.type === "HYBRID" && (
                <div className="text-sm">
                  + {(record.qty * parseFloat(record.price)).toLocaleString()}{" "}
                  บาท
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            {(record.qty * parseFloat(record.price)).toLocaleString()} บาท
          </div>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: "5%",
      render: (_, record) => (
        <Popconfirm
          title="ลบสินค้า"
          description="คุณต้องการลบสินค้านี้ออกจากตะกร้าหรือไม่?"
          onConfirm={() => handleDeleteItem(record.id)}
          okText="ใช่"
          cancelText="ไม่"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      ),
    },
  ];

  // Separate items into regular products and redemption items
  const regularProducts = useMemo(
    () => dataTable.filter((item) => !item.isRedemptionItem),
    [dataTable]
  );
  const redemptionItems = useMemo(
    () => dataTable.filter((item) => item.isRedemptionItem),
    [dataTable]
  );

  const mobileContent = useMemo(
    () => (
      <div className="flex flex-col gap-4">
        {/* Products Section */}
        {regularProducts.length > 0 && (
          <div className="mb-6">
            <div className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
              <span className="mr-2">🛍️</span>
              สินค้า ({regularProducts.length})
            </div>
            {regularProducts.map((item) => (
              <MobileCartItem
                key={item.id}
                item={item}
                index={0} // Index is no longer used for onChange, keeping for compatibility
                showCheckbox={true}
                isSelected={selectItems.some(
                  (selected) => selected.id === item.id
                )}
                onSelect={handleSingleItemSelect}
                onChangeQuantity={handleOnChangeInput}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        )}

        {/* Redemption Items Section */}
        {redemptionItems.length > 0 && (
          <div className="mb-6">
            {regularProducts.length > 0 && (
              <div className="border-t border-gray-200 my-4"></div>
            )}
            <div className="text-lg font-semibold text-purple-600 mb-3 flex items-center">
              <span className="mr-2">🎁</span>
              สินค้าแลกคะแนน ({redemptionItems.length})
            </div>
            <div className="bg-purple-50 rounded-lg p-3 mb-3">
              <div className="text-sm text-purple-700">
                สินค้าที่แลกด้วยคะแนนสะสม
              </div>
            </div>
            {redemptionItems.map((item) => (
              <MobileCartItem
                key={item.id}
                item={item}
                index={0} // Index is no longer used for onChange, keeping for compatibility
                showCheckbox={true}
                isSelected={selectItems.some(
                  (selected) => selected.id === item.id
                )}
                onSelect={handleSingleItemSelect}
                onChangeQuantity={handleOnChangeInput}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        )}
      </div>
    ),
    [
      regularProducts,
      redemptionItems,
      selectItems,
      handleSingleItemSelect,
      handleOnChangeInput,
      handleDeleteItem,
    ]
  );

  // Calculate totals for selected items (cash and points separately)
  const totals = useMemo(() => {
    return selectItems.reduce(
      (acc, cur) => {
        if (cur.isRedemptionItem && cur.redemptionItem) {
          // For redemption items, add points and cash separately
          acc.totalUsagePoints += cur.redemptionItem.pointsCost * cur.qty;
          if (cur.redemptionItem.type === "HYBRID") {
            acc.totalCash += parseFloat(cur.price) * cur.qty;
          }
        } else {
          // For regular products, add to cash total
          acc.totalCash += parseFloat(cur.price) * cur.qty;
        }
        return acc;
      },
      { totalCash: 0, totalUsagePoints: 0 }
    );
  }, [selectItems]);

  const handleClickCheckout = async () => {
    // Validate cart before proceeding to checkout
    // const isValid = await validateCartForCheckout();

    // if (!isValid) {
    //   // Validation errors are already set in state, they will be displayed
    //   return;
    // }

    setProductItemsSelected(selectItems);
    navigate(`/checkout`);
  };

  return (
    <NarinLayout>
      <NarinBreadcrumb
        items={[
          {
            title: "สินค้าทั้งหมด",
            path: "/",
          },
          {
            title: "ตะกร้าสินค้า",
          },
        ]}
      />
      <div className="w-full min-h-[500px] bg-white py-8 px-4">
        <div className="flex justify-between items-center mb-4">
          <div className="text-xl">ตะกร้าสินค้า</div>
          {/* {selectItems.length > 0 && (
            <div className="flex items-center gap-4">
              <Typography.Text className="text-sm text-gray-600">
                เลือกแล้ว {selectItems.length} รายการ
              </Typography.Text>
              <Popconfirm
                title="ลบสินค้าที่เลือก"
                description={`คุณต้องการลบสินค้า ${selectItems.length} รายการที่เลือกออกจากตะกร้าหรือไม่?`}
                onConfirm={handleBulkDelete}
                okText="ใช่"
                cancelText="ไม่"
              >
                <Button
                  danger
                  type="primary"
                  size="small"
                  className="hover:shadow-md transition-all duration-200 hover:scale-105"
                  icon={<DeleteOutlined />}
                >
                  ลบที่เลือก ({selectItems.length})
                </Button>
              </Popconfirm>
            </div>
          )} */}
        </div>

        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <div className="mb-4">
            <Alert
              message="การตรวจสอบรายการแลกซื้อ"
              description={
                <div>
                  <p className="mb-2">พบปัญหาในรายการแลกซื้อของคุณ:</p>
                  <ul className="list-disc list-inside">
                    {validationErrors.map((error, index) => (
                      <li key={index} className="text-sm">
                        {error}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-sm">
                    กรุณาแก้ไขปัญหาก่อนดำเนินการชำระเงิน
                  </p>
                </div>
              }
              type="warning"
              showIcon
              closable
              onClose={() => setValidationErrors([])}
            />
          </div>
        )}
        {screens.md ? (
          <div>
            {/* Products Section */}
            {regularProducts.length > 0 && (
              <div className="mb-8">
                <div className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="mr-2">🛍️</span>
                  สินค้า ({regularProducts.length})
                </div>
                <Table
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: selectItems
                      .filter((item) => !item.isRedemptionItem)
                      .map((item) => item.key),
                    onSelect: (_, __, selectedRows) =>
                      handleProductSectionSelect(selectedRows),
                    onSelectAll: (_, selectedRows) =>
                      handleProductSectionSelect(selectedRows),
                  }}
                  onRow={(record) => ({
                    onClick: (event) => {
                      // Prevent navigation when clicking on checkbox or delete button
                      if (
                        event.target instanceof HTMLElement &&
                        (event.target.closest('input[type="checkbox"]') ||
                          event.target.closest("button"))
                      ) {
                        return;
                      }
                      // Navigate to product detail page
                      navigate(`/product/${record.productId}`);
                    },
                    style: { cursor: "pointer" },
                    className:
                      "hover:bg-gray-50 transition-colors duration-200",
                    onMouseEnter: (e) => {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    },
                    onMouseLeave: (e) => {
                      e.currentTarget.style.backgroundColor = "";
                    },
                  })}
                  columns={columns}
                  dataSource={regularProducts}
                  pagination={false}
                />
              </div>
            )}

            {/* Redemption Items Section */}
            {redemptionItems.length > 0 && (
              <div className="mb-8">
                {regularProducts.length > 0 && (
                  <div className="border-t border-gray-200 my-6"></div>
                )}
                <div className="text-lg font-semibold text-purple-600 mb-4 flex items-center">
                  <span className="mr-2">🎁</span>
                  สินค้าแลกคะแนน ({redemptionItems.length})
                </div>
                <div className="bg-purple-50 rounded-lg p-3 mb-4">
                  <div className="text-sm text-purple-700">
                    สินค้าที่แลกด้วยคะแนนสะสม
                  </div>
                </div>
                <Table
                  rowSelection={{
                    type: "checkbox",
                    selectedRowKeys: selectItems
                      .filter((item) => item.isRedemptionItem)
                      .map((item) => item.key),
                    onSelect: (_, __, selectedRows) =>
                      handleRedemptionSectionSelect(selectedRows),
                    onSelectAll: (_, selectedRows) =>
                      handleRedemptionSectionSelect(selectedRows),
                  }}
                  onRow={(_record) => ({
                    onClick: (event) => {
                      // Prevent navigation when clicking on checkbox or delete button
                      if (
                        event.target instanceof HTMLElement &&
                        (event.target.closest('input[type="checkbox"]') ||
                          event.target.closest("button"))
                      ) {
                        return;
                      }
                      // Navigate to capsule market for redemption items
                      navigate("/capsule-market");
                    },
                    style: { cursor: "pointer" },
                    className:
                      "hover:bg-purple-50 transition-colors duration-200",
                    onMouseEnter: (e) => {
                      e.currentTarget.style.backgroundColor = "#faf5ff";
                    },
                    onMouseLeave: (e) => {
                      e.currentTarget.style.backgroundColor = "";
                    },
                  })}
                  columns={columns}
                  dataSource={redemptionItems}
                  pagination={false}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 mb-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={
                    dataTable.length > 0 &&
                    selectItems.length === dataTable.length
                  }
                  onChange={(e) => {
                    handleUnifiedSelectAll(e.target.checked);
                  }}
                  className="w-4 h-4"
                />
                <span>เลือกทั้งหมด</span>
              </div>
              {selectItems.length > 0 && (
                <Popconfirm
                  title="ลบสินค้าที่เลือก"
                  description={`คุณต้องการลบสินค้า ${selectItems.length} รายการที่เลือกออกจากตะกร้าหรือไม่?`}
                  onConfirm={handleBulkDelete}
                  okText="ใช่"
                  cancelText="ไม่"
                >
                  <Button
                    danger
                    type="primary"
                    size="small"
                    icon={<DeleteOutlined />}
                  >
                    ลบ ({selectItems.length})
                  </Button>
                </Popconfirm>
              )}
            </div>
            {mobileContent}
          </>
        )}
        <Row>
          <Col xs={24} md={{ span: 8, offset: 16 }}>
            {/* Cash earned points (only for cash purchases) */}
            {totals.totalCash > 0 && (
              <Row gutter={16}>
                <Col className="py-4 px-8 text-sm" span={12}>
                  คะแนนที่ได้ทั้งหมด
                </Col>
                <Col className="text-end py-4 px-8 text-sm" span={12}>
                  {Math.floor(totals.totalCash / 25).toLocaleString()} คะแนน
                </Col>
              </Row>
            )}
            {/* Points total (if any redemption items selected) */}
            {totals.totalUsagePoints > 0 && (
              <Row gutter={16}>
                <Col className="py-4 px-8 text-sm" span={12}>
                  คะแนนที่ใช้สิ่งแลกของ
                </Col>
                <Col
                  className="text-end py-4 px-8 text-sm text-purple-600 font-semibold"
                  span={12}
                >
                  {totals.totalUsagePoints.toLocaleString()} คะแนน
                </Col>
              </Row>
            )}
            {/* Cash total */}
            <Row gutter={16}>
              <Col className="bg-[#E8F8CCE8] py-4 px-8" span={12}>
                ราคารวมทั้งหมด
              </Col>
              <Col
                className="text-end bg-[#E8F8CCE8] py-4 px-8 font-semibold"
                span={12}
              >
                {totals.totalCash.toLocaleString()} บาท
              </Col>
            </Row>
            <Row justify={"end"} className="mt-8">
              <Col>
                <Button
                  size="large"
                  type="primary"
                  className="w-full md:w-[230px]"
                  disabled={selectItems.length === 0}
                  loading={isValidatingCart}
                  onClick={handleClickCheckout}
                >
                  {isValidatingCart ? "กำลังตรวจสอบ..." : "สั่งซื้อสินค้า"}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </NarinLayout>
  );
};
export default CartPage;
